/**
 * Utility functions for extracting and normalizing property details from Zillow data
 */

import { PropertyDetails } from '@/app/scheduler/SchedulerContext';
import { BridgeParcel } from '@/util/functions/fetching/callZillowAndMatch';
import { FoundationType } from '@/util/types/globalTypes';

/**
 * Finds the main building from Zillow data (building with most rooms)
 */
export function getMainBuilding(
  zillowData: BridgeParcel | null
): BridgeParcel['building'][0] | null {
  if (!zillowData?.building) return null;

  return zillowData.building.reduce((prev, current) => {
    const prevRooms = prev?.totalRooms || 0;
    const currentRooms = current?.totalRooms || 0;
    return currentRooms > prevRooms ? current : prev;
  }, zillowData.building[0]);
}

/**
 * Determines water type from Zillow building data
 */
export function extractWaterType(
  waterStr?: string
): 'city' | 'well' | undefined {
  if (!waterStr) return undefined;

  const normalized = waterStr.toLowerCase();
  if (normalized.includes('well')) return 'well';
  if (
    normalized.includes('municipal') ||
    normalized.includes('public') ||
    normalized.includes('city')
  ) {
    return 'city';
  }
  return undefined;
}

/**
 * Determines sewer type from Zillow building data
 */
export function extractSewerType(
  sewerStr?: string
): 'city' | 'septic' | undefined {
  if (!sewerStr) return undefined;

  const normalized = sewerStr.toLowerCase();
  if (normalized.includes('septic')) return 'septic';
  if (
    normalized.includes('municipal') ||
    normalized.includes('public') ||
    normalized.includes('city') ||
    normalized.includes('sewer')
  ) {
    return 'city';
  }
  return undefined;
}

/**
 * Determines foundation type from Zillow building data and areas
 */
export function extractFoundationType(
  foundationStr?: string,
  areas?: any[],
  landUseDescription?: string
): FoundationType | undefined {
  // First try to determine from foundation string
  if (foundationStr) {
    const normalized = foundationStr.toLowerCase();
    if (
      normalized.includes('basement') ||
      normalized.includes('bsmt') ||
      normalized.includes('full basement')
    ) {
      return 'basement';
    }
    if (normalized.includes('crawl') || normalized.includes('crawlspace')) {
      return 'crawlspace';
    }
    if (
      normalized.includes('slab') ||
      normalized.includes('concrete slab') ||
      normalized.includes('slab on grade')
    ) {
      return 'slab';
    }
    if (
      normalized.includes('elevated') ||
      normalized.includes('pier') ||
      normalized.includes('piling') ||
      normalized.includes('stilt') ||
      normalized.includes('pier and beam') ||
      normalized.includes('pier and post')
    ) {
      return 'elevated';
    }
  }

  // Check for condo indicators
  // Note: We can detect condo, but "not ground floor" requires manual input
  // For now, if it's a condo, we'll return condo-not-ground-floor as a default
  // The user can change it if they're on ground floor
  if (landUseDescription) {
    const normalizedLandUse = landUseDescription.toLowerCase();
    if (
      normalizedLandUse.includes('condo') ||
      normalizedLandUse.includes('condominium')
    ) {
      return 'condo-not-ground-floor';
    }
  }

  // Fallback: Check areas array for basement
  if (areas?.some((area: any) => area.type === 'Basement')) {
    return 'basement';
  }

  return undefined;
}

/**
 * Extracts all property details from Zillow data
 * This is the single source of truth for property data extraction
 */
export function extractPropertyDetails(
  zillowData: BridgeParcel | null
): PropertyDetails {
  const mainBuilding = getMainBuilding(zillowData);

  return {
    bedrooms: mainBuilding?.bedrooms || undefined,
    bedroomsVerified: false,
    bathrooms: mainBuilding?.baths || mainBuilding?.fullBaths || undefined,
    bathroomsVerified: false,
    hvacType: mainBuilding?.heating || undefined,
    hvacTypeVerified: false,
    acType: mainBuilding?.airConditioning || undefined,
    acTypeVerified: false,
    foundationType: extractFoundationType(
      mainBuilding?.foundation || undefined,
      zillowData?.areas,
      zillowData?.landUseDescription
    ),
    foundationTypeVerified: false,
    waterType: extractWaterType(mainBuilding?.water || undefined),
    waterTypeVerified: false,
    sewerType: extractSewerType(mainBuilding?.sewer || undefined),
    sewerTypeVerified: false,
    poolCount: zillowData?.pools?.length || 0,
    poolCountVerified: false,
    garageCount: zillowData?.garages?.length || 0,
    garageCountVerified: false,
    outbuildingCount:
      zillowData?.numberOfBuildings && zillowData.numberOfBuildings > 1
        ? zillowData.numberOfBuildings - 1
        : 0,
    outbuildingCountVerified: false,
  };
}

/**
 * Extracts address-specific data from Zillow
 */
export function extractAddressData(zillowData: BridgeParcel | null) {
  const mainBuilding = getMainBuilding(zillowData);

  const totalFinishedSqft =
    zillowData?.areas?.reduce((total, area) => {
      if (
        area.type === 'Zillow Calculated Finished Area' ||
        area.type === 'Basement'
      ) {
        return total + (area.areaSquareFeet || 0);
      }
      return total;
    }, 0) || 0;

  return {
    sqft: totalFinishedSqft,
    yearBuilt: mainBuilding?.yearBuilt || undefined,
    unitCount: mainBuilding?.noOfUnits || 1,
    foundation: extractFoundationType(
      mainBuilding?.foundation || undefined,
      zillowData?.areas,
      zillowData?.landUseDescription
    ),
    county: zillowData?.county?.trim() || undefined,
  };
}
